import { useNavigate } from "react-router-dom";
import { LogOut, ShieldCheck, Calendar, UserCircle, FileText, Link2 } from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import AvatarUploader from "@/components/AvatarUploader";
import ProfileEditor from "@/components/account/ProfileEditor";

function formatDate(value) {
  if (!value) return "—";
  try {
    const d = typeof value === "string" ? new Date(value) : value;
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[account] formatDate failed:", err?.message || err);
    return "—";
  }
}

const PROVIDER_LABEL = {
  password: "Email & password",
  google: "Google",
  both: "Email & password + Google",
};

export default function Account() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    // Navigate FIRST so ProtectedRoute doesn't redirect to /login mid-logout.
    navigate("/", { replace: true });
    await logout();
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="account-page">
      <Navbar />
      <main className="pt-24 lg:pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div>
            <p className="text-sm uppercase tracking-[0.18em] text-sky-700 font-medium">
              Account
            </p>
            <h1
              className="mt-2 text-3xl lg:text-4xl font-medium tracking-tight text-slate-900"
              data-testid="account-heading"
            >
              Your account
            </h1>
            <p className="mt-2 text-base text-slate-600">
              Manage your basic profile and session.
            </p>
          </div>

          <section className="mt-10 rounded-3xl border border-slate-200 bg-white p-7 lg:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <AvatarUploader
              user={user}
              onChange={() => {
                // Pull fresh /auth/me so navbar dropdown updates immediately.
                refreshUser?.();
              }}
              testidPrefix="account"
            />

            <div className="mt-8 pt-6 border-t border-slate-100">
              <p
                className="text-xl font-medium text-slate-900 truncate"
                data-testid="account-name"
              >
                {user.name || user.email.split("@")[0]}
              </p>
              <p
                className="text-sm text-slate-500 truncate"
                data-testid="account-email"
              >
                {user.email}
              </p>
            </div>

            <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Detail
                icon={ShieldCheck}
                label="Sign-in method"
                value={PROVIDER_LABEL[user.auth_provider] || user.auth_provider}
                testid="account-detail-provider"
              />
              <Detail
                icon={Calendar}
                label="Member since"
                value={formatDate(user.created_at)}
                testid="account-detail-created"
              />
            </dl>

            <div className="mt-8 pt-6 border-t border-slate-100">
              <h2 className="text-base font-medium text-slate-900">
                Personal details
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Update your contact info. Each change is recorded — the admin
                team keeps a history for security and support.
              </p>
              <ProfileEditor user={user} onUpdated={refreshUser} />
            </div>

            <div className="mt-10 pt-6 border-t border-slate-100 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <p className="text-sm text-slate-500">
                Signed in as{" "}
                <span className="font-medium text-slate-700">{user.email}</span>
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <RouterLink
                  to="/account/consents"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all"
                  data-testid="account-consents-link"
                  title="Manage which partner companies can read your career report."
                >
                  <Link2 className="h-4 w-4" />
                  Partner access
                </RouterLink>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/archive/responses.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all"
                  data-testid="account-responses-pdf-button"
                  title="Download a PDF of every answer you submitted in the questionnaire."
                >
                  <FileText className="h-4 w-4" />
                  Download your responses
                </a>
                <button
                  type="button"
                  onClick={onLogout}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all"
                  data-testid="account-logout-button"
                >
                  <LogOut className="h-4 w-4" />
                  Log out
                </button>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

function Detail({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid}>
      <dt className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </dt>
      <dd className="mt-1.5 text-base text-slate-900">{value}</dd>
    </div>
  );
}
